import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { SelfServiceSMBSignupModal } from './SelfServiceSMBSignupModal';
import { useSelfServiceSMBSignupModal } from './hooks/useSelfServiceSMBSignupModal';
import { scrubSelfServiceSMBSignupContext } from './utils/sentryScrub';

interface SelfServiceSMBSignupGateProps {
  // Where to navigate when the user clicks "Go to dashboard" on the
  // confirmation step. Inline mounts typically pass the current page URL
  // so the user lands back where they were trying to go.
  nextUrl?: string;
  // Fires when the user closes the modal. Callers use this to clean up
  // trigger state (e.g. strip ?requestConsultation=1 from the URL) so the
  // modal doesn't immediately re-mount on next render.
  onClose?: () => void;
}

// Inline mount wrapper for SelfServiceSMBSignupModal. Auto-opens on mount,
// owns its own open state, and delegates close-side cleanup to the parent
// via onClose. Use this from any page that wants to show the signup
// modal contextually (over its own content as the blurred backdrop)
// instead of routing the user to /signup/customer.
export function SelfServiceSMBSignupGate({
  nextUrl,
  onClose,
}: SelfServiceSMBSignupGateProps): JSX.Element | null {
  const [open, setOpen] = useState(true);
  // skipHasTeamBillingRedirect: inline mounts render their own page
  // content underneath; we don't want the hook bouncing fully-signed-up
  // users away from the page they're already on.
  const handleClose = useCallback(() => {
    setOpen(false);
    onClose?.();
  }, [onClose]);

  const { modalProps, skipModal } = useSelfServiceSMBSignupModal({
    nextUrl,
    skipHasTeamBillingRedirect: true,
    onClose: handleClose,
  });

  // A signed-in viewer with nothing to resume would get the anonymous
  // `account` step, and skipHasTeamBillingRedirect has turned off the hook's
  // bounce (ENG-5769). Pages should route that viewer elsewhere before
  // mounting the gate, as SearchPage does. Reaching here means a page didn't,
  // so report it once and close, letting the caller's onClose move them on
  // rather than leaving them on an empty page.
  const skipReportedRef = useRef(false);
  useEffect(() => {
    if (!skipModal || skipReportedRef.current) return;
    skipReportedRef.current = true;
    Sentry.captureMessage('SelfServiceSMBSignupGate mounted for a viewer with nothing to resume', {
      level: 'warning',
      tags: scrubSelfServiceSMBSignupContext({ flow_kind: 'self_serve' }) as Record<string, string>,
    });
    handleClose();
  }, [skipModal, handleClose]);

  if (skipModal) return null;

  return <SelfServiceSMBSignupModal {...modalProps} open={open} onClose={handleClose} />;
}
